import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import { transactionListItemSchema } from "@common/schemas/transaction.schema";
import {
    AppliedTransactionsQuerySchema,
    AppliedTransferTransactionsQuerySchema,
    DeleteRuleErrorSchema,
    MatchingTransactionsQuerySchema,
    MatchingTransferTransactionsQuerySchema,
    NormalizedDescriptionsResponseSchema,
    RuleCreationSchema,
    RuleGroupCreateSchema,
    RuleGroupUpdateSchema,
    RuleOrderUpdateSchema,
    RuleUpdateQuerySchema,
    UpdateRuleNoteSchema,
} from "@common/schemas/rule.schema";
import {
    TransactionRuleReviewWithReviewer,
    transactionRuleReviewPathParamsSchema,
    transactionRuleReviewResponseSchema,
    transactionRuleReviewRevokeResponseSchema,
    transactionRuleReviewsResponseSchema,
} from "@common/schemas/transactionRuleReview.schema";
import { Counterparty } from "../types/counterparty";
import { TransferPreviewTransactionDto } from "../dto/transactionRule/transfer-preview-transaction.dto";
import { TransactionRuleDto } from "../dto/transactionRule/transaction-rule.dto";
import { TransactionRuleGroupDto } from "../dto/transactionRule/transaction-rule-group.dto";

const transferPreviewTransactionDtoSchema =
    z.custom<TransferPreviewTransactionDto>(
        (val) =>
            typeof val === "object" &&
            val !== null &&
            "id" in val &&
            "date" in val,
    );

const AppliedTransferTransactionsAllowResponseSchema = z.object({
    mode: z.literal("allow"),
    matches: z.array(
        z.object({
            transaction: transferPreviewTransactionDtoSchema,
            match: transferPreviewTransactionDtoSchema,
        }),
    ),
    total: z.number(),
});

const AppliedTransferTransactionsDisallowResponseSchema = z.object({
    mode: z.literal("disallow"),
    transactions: z.array(transactionListItemSchema),
    total: z.number(),
});

export const MatchingTransactionsResponseSchema = z.object({
    transactions: z.array(transactionListItemSchema),
    total: z.number(),
});

export type MatchingTransactionsResponse = z.infer<
    typeof MatchingTransactionsResponseSchema
>;

export const AppliedTransferTransactionsResponseSchema = z.discriminatedUnion(
    "mode",
    [
        AppliedTransferTransactionsAllowResponseSchema,
        AppliedTransferTransactionsDisallowResponseSchema,
    ],
);

export type AppliedTransferTransactionsResponse = z.infer<
    typeof AppliedTransferTransactionsResponseSchema
>;

const c = initContract();

export const ruleContract = c.router(
    {
        getRuleGroups: {
            method: "GET",
            path: "/:workspaceId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            responses: {
                200: c.type<{
                    ruleGroups: TransactionRuleGroupDto[];
                    counterparties: Counterparty[];
                    normalizedDescriptions: Array<{
                        id: string;
                        normalizedDescription: string;
                    }>;
                    transactionCounts: Record<string, number>;
                    matchingCounts: Record<string, number>;
                    ruleReviews: Record<
                        string,
                        TransactionRuleReviewWithReviewer[]
                    >;
                }>(),
            },
        },
        createRule: {
            method: "POST",
            path: "/:workspaceId/rule/",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            responses: {
                201: c.type<{ rule: TransactionRuleDto }>(),
                400: ErrorMessageSchema,
                409: ErrorMessageSchema,
            },
            body: RuleCreationSchema,
        },
        updateRule: {
            method: "PATCH",
            path: "/:workspaceId/rule/:ruleId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                ruleId: z.string().uuid(),
            }),
            responses: {
                200: c.type<{ rule: TransactionRuleDto }>(),
            },
            body: RuleUpdateQuerySchema,
        },
        changeRuleOrder: {
            method: "PATCH",
            path: "/:workspaceId/rule/:ruleId/order",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                ruleId: z.string().uuid(),
            }),
            responses: {
                200: c.type<{ rule: TransactionRuleDto }>(),
            },
            body: RuleOrderUpdateSchema,
        },
        updateRuleNote: {
            method: "PATCH",
            path: "/:workspaceId/rule/:ruleId/note",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                ruleId: z.string().uuid(),
            }),
            responses: {
                200: c.type<{ rule: TransactionRuleDto }>(),
                404: ErrorMessageSchema,
            },
            body: UpdateRuleNoteSchema,
        },
        getRuleReviews: {
            method: "GET",
            path: "/:workspaceId/rule/:ruleId/reviews",
            pathParams: transactionRuleReviewPathParamsSchema,
            responses: {
                200: transactionRuleReviewsResponseSchema,
                404: ErrorMessageSchema,
            },
        },
        addRuleReview: {
            method: "POST",
            path: "/:workspaceId/rule/:ruleId/review",
            pathParams: transactionRuleReviewPathParamsSchema,
            body: c.noBody(),
            responses: {
                200: transactionRuleReviewResponseSchema,
                404: ErrorMessageSchema,
            },
        },
        revokeRuleReview: {
            method: "DELETE",
            path: "/:workspaceId/rule/:ruleId/review",
            pathParams: transactionRuleReviewPathParamsSchema,
            body: c.noBody(),
            responses: {
                200: transactionRuleReviewRevokeResponseSchema,
                404: ErrorMessageSchema,
            },
        },
        deleteRule: {
            method: "DELETE",
            path: "/:workspaceId/rule/:ruleId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                ruleId: z.string().uuid(),
            }),
            responses: {
                200: c.noBody(),
                400: DeleteRuleErrorSchema,
            },
        },
        getAccountingDefaultRules: {
            method: "GET",
            path: "/:workspaceId/accounting-default-rules",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            responses: {
                200: c.type<{
                    ruleGroup: TransactionRuleGroupDto;
                    transactionCounts: Record<string, number>;
                }>(),
            },
        },
        getMatchingTransactions: {
            method: "POST",
            path: "/:workspaceId/get-matching-transactions",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            responses: {
                200: MatchingTransactionsResponseSchema,
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
            body: MatchingTransactionsQuerySchema,
        },
        getAppliedTransactions: {
            method: "POST",
            path: "/:workspaceId/rule/:ruleId/applied-transactions",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                ruleId: z.string().uuid(),
            }),
            responses: {
                200: z.object({
                    transactions: z.array(transactionListItemSchema),
                    total: z.number(),
                }),
                404: ErrorMessageSchema,
            },
            body: AppliedTransactionsQuerySchema,
        },
        getMatchingTransferTransactions: {
            method: "POST",
            path: "/:workspaceId/get-matching-transfer-transactions",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            responses: {
                200: c.type<{
                    matches: Array<{
                        transaction: TransferPreviewTransactionDto;
                        match: TransferPreviewTransactionDto;
                    }>;
                    total: number;
                }>(),
                400: ErrorMessageSchema,
            },
            body: MatchingTransferTransactionsQuerySchema,
        },
        getAppliedTransferTransactions: {
            method: "POST",
            path: "/:workspaceId/rule/:ruleId/applied-transfer-transactions",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                ruleId: z.string().uuid(),
            }),
            responses: {
                200: AppliedTransferTransactionsResponseSchema,
                404: ErrorMessageSchema,
            },
            body: AppliedTransferTransactionsQuerySchema,
        },
        createRuleGroup: {
            method: "POST",
            path: "/:workspaceId/group/",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            responses: {
                201: c.type<{ rule: TransactionRuleGroupDto }>(),
            },
            body: RuleGroupCreateSchema,
        },
        updateRuleGroup: {
            method: "PATCH",
            path: "/:workspaceId/group/:groupId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                groupId: z.string().uuid(),
            }),
            responses: {
                200: c.type<{ rule: TransactionRuleGroupDto }>(),
            },
            body: RuleGroupUpdateSchema,
        },
        deleteRuleGroup: {
            method: "DELETE",
            path: "/:workspaceId/group/:groupId",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
                groupId: z.string().uuid(),
            }),
            responses: {
                200: c.noBody(),
            },
        },
        getNormalizedDescriptions: {
            method: "GET",
            path: "/:workspaceId/normalized-descriptions",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            query: z.object({
                entityId: z.coerce.number().optional(),
            }),
            responses: {
                200: NormalizedDescriptionsResponseSchema,
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
    },
    {
        pathPrefix: "/rule-groups",
    },
);
